import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { FlightLogFormBuilderContext } from 'src/app/flight-logs/flight-log-form-helper';
import {
  getFlightDurationInMs,
  getSumFlightHoursForLogEntry,
  getTotalFlightTime,
  sumFlightHours,
} from 'src/app/shared/flight-log-api/flight-logs/flight-log-client-model-helpers';
import {
  FlightLogEntryDto,
  PilotLandingsDto,
  PilotLogEntryDto,
} from 'src/app/shared/flight-log-api/flight-logs/flight-log-client.model';
import { isPilot } from 'src/app/shared/flight-log-api/shared/shared-client.model';
import { FlightLogEntity } from 'src/store/flight-log/flight-log.model';
import { forEachControl, getParentAtDepth } from 'src/util/angular/ng-forms-helpers';
import { toggleErrors } from 'src/util/angular/ng-forms-validation';
import {
  convertMillisecondsToHours,
  hasMinimumYear,
  isDate,
  millisecondsInHour,
} from 'src/util/dateLib';
import { round } from 'src/util/math';

export function validateCrewMemberHasNvg(entry: UntypedFormGroup): ValidationErrors | null {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const nvgSelected = entry.get('entryDetail.attributes.nvg')!.value;
  if (nvgSelected != null) {
    const crewOnboard = entry.get('entryDetail.crewOnboard') as UntypedFormArray;
    return {
      noCrewWithNvg: true,
    };
  }
  return null;
}

export function validateLegDurationsSumToTotalFlightTime(
  flightLogFormGroup: AbstractControl,
): ValidationErrors | null {
  const flightLog: FlightLogEntity = flightLogFormGroup.value;
  const flightDurationInHours = convertMillisecondsToHours(getFlightDurationInMs(flightLog));
  const totalFlightTimeInHours = getTotalFlightTime(flightLog);
  if (totalFlightTimeInHours != null && totalFlightTimeInHours != null) {
    // It is possible for the hobbs time (totalFlightTimeInHours) to be > flightDurationInHours (FAA time, which includes taxiing)
    // In the case where the pilot has the engine running on the ground and tilts the collective enough to
    // cause the hobbs to tick. We're assuming this would never exceed an hour of actual time in a single flight.
    const margin = 1.0;
    const maxVal = round(flightDurationInHours + margin, 1);
    const isValidValue = totalFlightTimeInHours <= maxVal;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const totalFlightTimeCtrl = flightLogFormGroup.get('maintenanceSummary.totalFlightTime')!;
    const flightDurationDisplay = round(flightDurationInHours, 2);
    const error = {
      flightDurationMatchTotalFlightTime: {
        message: `The Flight Time (${totalFlightTimeInHours} hours) is greater than the total of the depature/arrival durations for the flight (${flightDurationDisplay} hours) plus 1 hour of margin.`,
      },
    };
    toggleErrors(!isValidValue, totalFlightTimeCtrl, error);
  }
  return null;
}

export function pilotLogEntryValidator(context: FlightLogFormBuilderContext): ValidatorFn | null {
  return Validators.compose([pilotFlightHours(context), pilotLandings()]);
}
//  = Validators.compose([pilotFlightHours]);

export function pilotLandings(): ValidatorFn {
  return (pilotLogEntryFormGroup: AbstractControl) => {
    const landingsCtrl = pilotLogEntryFormGroup.get('landings') as UntypedFormGroup;
    const hnvgo = (pilotLogEntryFormGroup.value as PilotLogEntryDto).hnvgo;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const runOnCtrl = landingsCtrl.get('runOn')!;
    const { day, night, runOn } = landingsCtrl.value as PilotLandingsDto;
    const totalLandings = day + night + (hnvgo != null ? hnvgo : 0);
    const isValidValue = totalLandings >= runOn;

    toggleErrors(!isValidValue, runOnCtrl, {
      runOnLandingsMustNotExceedDayNightLandings: {
        message: `Run on landings (${runOn}) cannot exceed the total landings (${totalLandings}).`,
      },
    });

    return null;
  };
}

interface LegDurationInfo {
  fromWaypointFormGroup: UntypedFormGroup;
  toWaypointFormGroup: UntypedFormGroup;
  departureTimeFormControl: UntypedFormControl;
  arrivalTimeFormControl: UntypedFormControl;
  departureTime: Date | null;
  arrivalTime: Date | null;
  legDurationInMs: number | null;
  legDurationInHours: number | null;
  arrivalTimeIsValid: boolean;
  departureTimeIsValid: boolean;
}

function getLogEntryWaypointInfo(logEntryFormGroup: AbstractControl): LegDurationInfo | null {
  if (logEntryFormGroup == null) {
    return null;
  }
  const logEntry: FlightLogEntryDto = logEntryFormGroup.value;
  if (logEntry.entryDetail.fromWaypointId == null || logEntry.entryDetail.toWaypointId == null) {
    return null;
  }
  const fromWaypointFormGroup = getWaypointFormGroup(
    logEntry.entryDetail.fromWaypointId,
    logEntryFormGroup,
  );
  const toWaypointFormGroup = getWaypointFormGroup(
    logEntry.entryDetail.toWaypointId,
    logEntryFormGroup,
  );
  if (fromWaypointFormGroup == null || toWaypointFormGroup == null) {
    return null;
  }
  const departureTimeFormControl = fromWaypointFormGroup.get('departureTime') as UntypedFormControl;
  const departureTime: Date = departureTimeFormControl.value;
  const departureTimeIsValid = isDate(departureTime);

  const arrivalTimeFormControl = toWaypointFormGroup.get('arrivalTime') as UntypedFormControl;
  const arrivalTime: Date = arrivalTimeFormControl.value;
  const arrivalTimeIsValid = isDate(arrivalTime);
  let legDurationInMs = null;
  let legDurationInHours = null;
  if (departureTimeIsValid && arrivalTimeIsValid) {
    legDurationInMs = arrivalTime.getTime() - departureTime.getTime();
    legDurationInHours = round(convertMillisecondsToHours(legDurationInMs), 2);
  }
  return {
    fromWaypointFormGroup,
    toWaypointFormGroup,
    departureTimeFormControl,
    arrivalTimeFormControl,
    departureTime,
    arrivalTime,
    arrivalTimeIsValid,
    departureTimeIsValid,
    legDurationInMs,
    legDurationInHours,
  };
}

export function flightLogValidators(context: FlightLogFormBuilderContext): ValidatorFn | null {
  return Validators.compose([
    context.flightLog.mode === 'EntryPerLeg' ? validateLegDurationsSumToTotalFlightTime : null,
    validatePatientAboard(context),
  ]);
}

export function flightLogEntryValidators(context: FlightLogFormBuilderContext): ValidatorFn | null {
  return Validators.compose([
    arrivalDepartureTimesRequired(context),
    crewMemberMustHaveNvgIfNvgAttributeIsSelected,
    originDepartureTimePrecedesDestinatinArrivalTime,
    dayNightHoursSumToLegDuration,
    individualPilotLogEntriesDoNotExceedLegDuration,
    legMustHavePilotAboard,
  ]);
}

function crewMemberMustHaveNvgIfNvgAttributeIsSelected(
  flightLogEntryFormGroup: AbstractControl,
): ValidationErrors | null {
  const flightLogEntry: FlightLogEntryDto = flightLogEntryFormGroup.value;
  const nvgSelected = flightLogEntry.entryDetail.attributes.nvg;
  if (nvgSelected) {
    const isValueValid = flightLogEntry.entryDetail.crewOnboard.find((c) => c.isNvg) !== undefined;
    if (!isValueValid) {
      return {
        crewMemberNvg: {
          message: 'NVG is enabled for this leg but no crew member has NVG enabled.',
        },
      };
    }
  }
  return null;
}

function getWaypointFormGroup(
  waypointId: string,
  logEntryFormGroup: AbstractControl,
): UntypedFormGroup | null {
  const flightLog =
    logEntryFormGroup != null
      ? logEntryFormGroup.parent != null
        ? logEntryFormGroup.parent.parent
        : null
      : null;
  if (flightLog != null) {
    const waypoints = flightLog.get('waypoints') as UntypedFormArray;
    const waypoint = waypoints.controls.find((c) => c.value.id === waypointId);
    return waypoint as UntypedFormGroup;
  } else {
    return null;
  }
}

export function hnvgoNonDecimalValidator() {
  return (control: UntypedFormControl): ValidationErrors | null => {
    if (control != null && control.value != null) {
      const hnvgo: number = control.value;
      const isValueValid = hnvgo % 1 === 0;
      if (!isValueValid) {
        const error = {
          hnvgo: {
            message: 'Please enter non decimal numbers only.',
          },
        };
        return error;
      }
    }
    return null;
  };
}

function arrivalDepartureTimesRequired(context: FlightLogFormBuilderContext): ValidatorFn {
  return (flightLogEntryFormGroup: AbstractControl) => {
    const minimumDateValidationState = {
      minimumDate: {
        message:
          'Not a valid date time. Please be sure to enter all 4 digits of the year, 1/1/2017 rather than 1/1/17.',
      },
    };

    if (context.flightLog.mode !== 'EntryPerLeg') {
      // Validation only applies to entry per leg
      return null;
    }

    const waypointInfo = getLogEntryWaypointInfo(flightLogEntryFormGroup);
    if (waypointInfo == null) {
      // Form not yet initialized
      return null;
    }
    const { arrivalTimeFormControl, departureTimeFormControl } = waypointInfo;

    if (
      arrivalTimeFormControl != null &&
      context.user.flightLogFeatures?.isFlighTimeRequired(context.flightLog.companyId)
    ) {
      const arrivalTime = arrivalTimeFormControl.value;
      const isValidDate = isDate(arrivalTime);
      toggleErrors(!isValidDate, arrivalTimeFormControl, {
        required: {
          message: 'Arrival time is required.',
        },
      });
      if (isValidDate) {
        const meetsMin = hasMinimumYear(arrivalTime);
        toggleErrors(!meetsMin, departureTimeFormControl, minimumDateValidationState);
      }
    }
    if (
      departureTimeFormControl != null &&
      context.user.flightLogFeatures?.isFlighTimeRequired(context.flightLog.companyId)
    ) {
      const departureTime = departureTimeFormControl.value;
      const isValidDate = isDate(departureTime);
      toggleErrors(!isValidDate, departureTimeFormControl, {
        required: {
          message: 'Departure time is required.',
        },
      });
      if (isValidDate) {
        const meetsMin = hasMinimumYear(departureTime);
        toggleErrors(!meetsMin, departureTimeFormControl, minimumDateValidationState);
      }
    }
    return null;
  };
}

function originDepartureTimePrecedesDestinatinArrivalTime(
  flightLogEntryFormGroup: AbstractControl,
): ValidationErrors | null {
  const waypointInfo = getLogEntryWaypointInfo(flightLogEntryFormGroup);
  if (waypointInfo == null) {
    return null;
  }
  const {
    arrivalTimeFormControl,
    departureTimeFormControl,
    departureTimeIsValid,
    arrivalTimeIsValid,
    legDurationInMs,
  } = waypointInfo;

  if (arrivalTimeIsValid && departureTimeIsValid) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    toggleErrors(legDurationInMs! <= 0, [arrivalTimeFormControl, departureTimeFormControl], {
      arrivalDepartureTimeSequence: {
        message: 'The destination arrival time must follow the origin depature time.',
      },
    });
  }

  return null;
}

function dayNightHoursSumToLegDuration(
  flightLogEntryFormGroup: AbstractControl,
): ValidationErrors | null {
  const flightLogEntry: FlightLogEntryDto = flightLogEntryFormGroup.value;
  const flightLogFormGroup =
    getFlightLogEntryFormGroupFromLogEntryFormGroup(flightLogEntryFormGroup);
  if (
    // If we get a null form group, form hasn't fully initialized and not ready for validation
    flightLogFormGroup == null ||
    // Validation only applies to entry per leg forms. TODO: Consider conditionally
    // applying validators based on form mode instead of check within validator
    (flightLogFormGroup.value as FlightLogEntity).mode !== 'EntryPerLeg'
  ) {
    return null;
  }
  const waypointInfo = getLogEntryWaypointInfo(flightLogEntryFormGroup);
  if (waypointInfo == null) {
    return null;
  }
  const { legDurationInMs, legDurationInHours } = waypointInfo;
  if (legDurationInMs == null) {
    return null;
  }
  const legFlightHours = getSumFlightHoursForLogEntry(flightLogEntry);
  const legDayNightHoursSumInMs = legFlightHours.dayNightHoursSumInMs;
  const legDayNightHoursSum = legFlightHours.dayNightHoursSum;
  const isSumAllPilotsValid =
    legDayNightHoursSumInMs - legDayNightHoursSum + millisecondsInHour / 100 >= 0;

  forEachControl(
    flightLogEntryFormGroup.get('pilotLogEntries') as UntypedFormArray,
    (pilotLogEntryFormGroup: UntypedFormGroup) => {
      const pilotLogEntry: PilotLogEntryDto = pilotLogEntryFormGroup.value;
      const { dayNightCtrls } = getFlightHourControls(pilotLogEntryFormGroup);
      toggleErrors(!isSumAllPilotsValid, dayNightCtrls, {
        flghtHoursShouldMatchLegDuration: {
          message: `The total day/night aided/night unaided flight hours for the leg (${legDayNightHoursSum}) should be equal to or greater than the departure/arrival time duration (${legDurationInHours} hours).`,
        },
      });
    },
  );
  return null;
}

function individualPilotLogEntriesDoNotExceedLegDuration(
  flightLogEntryFormGroup: AbstractControl,
): ValidationErrors | null {
  const waypointInfo = getLogEntryWaypointInfo(flightLogEntryFormGroup);
  if (waypointInfo == null) {
    return null;
  }
  const { legDurationInMs, legDurationInHours } = waypointInfo;
  if (legDurationInMs == null) {
    return null;
  }

  forEachControl(
    flightLogEntryFormGroup.get('pilotLogEntries') as UntypedFormArray,
    (pilotLogEntryFormGroup: UntypedFormGroup) => {
      const pilotLogEntry: PilotLogEntryDto = pilotLogEntryFormGroup.value;
      const { dayNightHoursSumInMs, dayNightHoursSum } = sumFlightHours([
        pilotLogEntry.flightHours,
      ]);
      const { dayNightCtrls } = getFlightHourControls(pilotLogEntryFormGroup);
      const durationThresholdInMs = millisecondsInHour / 2; // 30 min. threshold
      const durationWithThresholdInHours =
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        legDurationInHours! + durationThresholdInMs / 1000 / 60 / 60; // duration hrs + threshold hrs.
      const isValueValid = dayNightHoursSumInMs - legDurationInMs - durationThresholdInMs <= 0;
      toggleErrors(!isValueValid, dayNightCtrls, {
        pilotHoursCannotExceedLegDuration: {
          message: `The total day/night aided/night unaided flight hours for this pilot (${dayNightHoursSum}) cannot exceed the departure/arrival time duration of (${durationWithThresholdInHours} hours).`,
        },
      });
    },
  );
  return null;
}

export function getFlightHourControls(pilotLogEntryFormGroup: AbstractControl) {
  const allControls = [
    'day',
    'nightAided',
    'nightUnaided',
    'part91',
    'part135',
    'ifrImc',
    'ifrSim',
    'part133HookDayHours',
    'part133HookNightHours',
    'part133HoistDayHours',
    'part133HoistNightHours',
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  ].map((name) => pilotLogEntryFormGroup.get(`flightHours.${name}`)!);
  const [
    dayCtrl,
    nightAidedCtrl,
    nightUnaidedCtrl,
    part91Ctrl,
    part135Ctrl,
    ifrImcCtrl,
    ifrSimCtrl,
    part133HookDayHoursCtrl,
    part133HookNightHoursCtrl,
    part133HoistDayHoursCtrl,
    part133HoistNightHoursCtrl,
  ] = allControls;
  const dayNightCtrls = [dayCtrl, nightAidedCtrl, nightUnaidedCtrl];
  const ifrCtrls = [ifrImcCtrl, ifrSimCtrl];
  const partCtrls = [part91Ctrl, part135Ctrl];
  const part133Ctrls = [
    part133HookDayHoursCtrl,
    part133HookNightHoursCtrl,
    part133HoistDayHoursCtrl,
    part133HoistNightHoursCtrl,
  ];
  return {
    dayCtrl,
    nightAidedCtrl,
    nightUnaidedCtrl,
    part91Ctrl,
    part135Ctrl,
    ifrImcCtrl,
    ifrSimCtrl,
    dayNightCtrls,
    partCtrls,
    allControls,
    ifrCtrls,
    part133HookDayHoursCtrl,
    part133HookNightHoursCtrl,
    part133HoistDayHoursCtrl,
    part133HoistNightHoursCtrl,
  };
}

export function pilotFlightHours(context: FlightLogFormBuilderContext): ValidatorFn {
  return (pilotLogEntryFormGroup: AbstractControl) => {
    const pilotLogEntry: PilotLogEntryDto = pilotLogEntryFormGroup.value;
    const { dayNightHoursSum, ifrHoursSum, part91part135HoursSum } = sumFlightHours([
      pilotLogEntry.flightHours,
    ]);

    const { partCtrls, dayNightCtrls, ifrCtrls } = getFlightHourControls(pilotLogEntryFormGroup);

    if (context.features.part135part91) {
      toggleErrors(dayNightHoursSum !== part91part135HoursSum, [...partCtrls, ...dayNightCtrls], {
        dayNightHoursEqualPart91Part135Hours: {
          message: `Part 91/135 hours (${part91part135HoursSum}) should equal day/night aided/night unaided hours (${dayNightHoursSum}).`,
        },
      });
    }

    toggleErrors(ifrHoursSum > dayNightHoursSum, ifrCtrls, {
      part91part135HoursShouldEqualDayNightHours: {
        message: `Total IFR IMC and IFR SIM flight hours (${ifrHoursSum}) cannot be greater than the total day/night aided/night unaided hours (${dayNightHoursSum}).`,
      },
    });

    return null;
  };
}

function getFlightLogEntryFormGroupFromLogEntryFormGroup(flightLogEntryFormGroup: AbstractControl) {
  return getParentAtDepth(flightLogEntryFormGroup, 2);
}

function legMustHavePilotAboard(flightLogEntryFormGroup: AbstractControl) {
  const flightLogFormGroup =
    getFlightLogEntryFormGroupFromLogEntryFormGroup(flightLogEntryFormGroup);
  if (flightLogFormGroup == null) {
    return null;
  }
  const flightLog: FlightLogEntity = flightLogFormGroup.value;
  const pilotCrewMemberIds = flightLog.flightCrew.filter((c) => isPilot(c.role)).map((c) => c.id);

  const flightLogEntry: FlightLogEntryDto = flightLogEntryFormGroup.value;
  const isValueValid =
    flightLogEntry.entryDetail.crewOnboard.find((crew) =>
      pilotCrewMemberIds.includes(crew.flightCrewId),
    ) !== undefined;

  if (!isValueValid) {
    return {
      pilotMustBeOnboard: {
        message: 'At least one Pilot must be on board.',
      },
    };
  }
  return null;
}

export function departureTimeMustFollowArrivalTime(waypointFormGroup: UntypedFormGroup): null {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const arrivalTimeCtrl = waypointFormGroup.get('arrivalTime')!;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const departureTimeCtrl = waypointFormGroup.get('departureTime')!;
  const arrivalTime: Date = arrivalTimeCtrl.value;
  const departureTime: Date = departureTimeCtrl.value;

  const isValueValid =
    !isDate(arrivalTime) ||
    !isDate(departureTime) ||
    // Only validate if both arrival and departure time are dates
    departureTime.getTime() - arrivalTime.getTime() > 0;

  toggleErrors(!isValueValid, arrivalTimeCtrl, {
    arrivalTimeMustNotPrecedPreviousDepartureTime: {
      message: 'Arrival time must precede the departure time for the following leg.',
    },
  });
  toggleErrors(!isValueValid, departureTimeCtrl, {
    departureTimeMustFollowPreviousArrivalTime: {
      message: 'Departure time must follow the arrival time for the previous leg.',
    },
  });
  return null;
}

function validatePatientAboard(context: FlightLogFormBuilderContext): ValidatorFn {
  return (control: AbstractControl): any | null => {
    if (control != null && control.value != null) {
      const flightLog = control.value as FlightLogEntity;

      const validate = context.features.validatePatientOnboard;
      const isPatientTransport =
        flightLog.flightType != null && /^(PatientTransport)/.test(flightLog.flightType);
      const legsWithPatient = flightLog.entries.filter((leg) => leg.entryDetail.isPatientOnboard);

      if (validate && isPatientTransport && legsWithPatient.length <= 0) {
        const error = {
          patientAboard: {
            message:
              'Flight Type is a Patient Transport, however no patient is marked aboard on any leg. Please validate the flight type and patient status',
          },
        };
        return error;
      }
    }
    return null;
  };
}
